import React from 'react'
import PropTypes from 'prop-types'
import gql from 'graphql-tag'
import {NavLink} from 'react-router-dom'
import {useMutation} from '@apollo/react-hooks'
import {message} from 'antd'
import {EditOutlined} from '@ant-design/icons'
import {groupBy} from 'lib/util'
import PageNotFound from 'shared/components/PageNotFound'
import Layout from 'components/shared/Layout'
import Query from 'components/shared/Query'
import Heading from 'components/shared/Heading'
import EstateHeader from 'components/shared/EstateHeader'
import Button from 'components/shared/Button'
import CreateWebOrderButton from 'components/shared/CreateWebOrderButton'
import CreateCollageButton from 'components/shared/CreateCollageButton'
import ResponsiveNavLayout from 'components/shared/ResponsiveNavLayout'
import ProductState from 'components/shared/ProductState'
import EstateContext from './EstateContext'
import './index.scss'

const createAiStudioSessionMutation = gql`
  mutation CreateAiStudioSession($estateId: ID!) {
    CreateAiStudioSession(estateId: $estateId)
  }
`

const CreateAiStudioButton = ({estateId}) => {
  const [createSession, {loading}] = useMutation(createAiStudioSessionMutation)

  const handleClick = () => {
    createSession({variables: {estateId}})
      .then(({data}) => {
        const url = data.CreateAiStudioSession
        if (url) {
          window.open(url)
        } else {
          message.error('Kunne ikke opprette AI Studio-sesjon')
        }
      })
      .catch(() => {
        message.error('Noe gikk galt. Prøv igjen senere.')
      })
  }

  return (
    <Button
      className="create-ai-studio-button"
      icon={<EditOutlined />}
      loading={loading}
      onClick={handleClick}
    >
      EFKT AI Studio
    </Button>
  )
}

export const estateQuery = gql`
  query EstateQuery($id: ID!, $referer: String) {
    estate(id: $id) {
      id
      invisoRef
      customerRef
      streetAddress
      customer {
        id
        canCreateOrders
        enableAreaPhotos
      }
      segment {
        id
        name
        backgroundColor
        iconUrl
      }
      products(toCustomer: true) {
        edges {
          node {
            id
            name
            state
            requiresUserInput
            canProvideUserInput
            ...on PhotoEditingBasedProduct {supplemental}
            ...on PhotoBasedProduct {quantity}
            ...on FloorPlansProduct {quantity}
          }
        }
      }
      createCollageUrl(referer: $referer)
      canCreateAiStudio
    }
  }
`

const ProductLink = ({match, product}) => (
  <NavLink
    to={`${match.url}/produkt/${product.id}`}
    className="product"
  >
    <span className="name">
      {product.name}{product.quantity ? ` (${product.quantity})` : ''}
    </span>
    <ProductState product={product} />
  </NavLink>
)

const EstateLayout = ({match, children}) => {
  return (
    <Query
      inlineLoading
      inlineError
      query={estateQuery}
      variables={{id: match.params.estateId, referer: '_referer_'}}
    >
      {({getData}) => {
        const estate = getData('estate')
        if (!estate) {
          return (
            <Layout>
              <PageNotFound />
            </Layout>
          )
        }

        const backTo = match.isExact ? null : `/oppdrag/${estate.id}`

        const products = estate.products.edges
          .map(e => e.node)
          // Put products that requires input on top
          .sort((a, b) => b.requiresUserInput - a.requiresUserInput)

        const groupedProducts = groupBy(products, p => p.requiresUserInput.toString())

        return (
          <EstateContext.Provider value={{estate, products}}>
            <Layout documentTitle={estate.streetAddress} backToPath={backTo}>
              <ResponsiveNavLayout
                isRoot={match.isExact}
                className="estate-layout"
                header={
                  <EstateHeader
                    streetAddress={estate.streetAddress}
                    customerRef={estate.customerRef}
                    invisoRef={estate.invisoRef}
                    segment={estate.segment}
                  />
                }
                sidebar={
                  <>
                    <nav>
                      {groupedProducts['true'] && (
                        <>
                          <Heading level={3}>Ikke fullførte bestillinger</Heading>
                          {groupedProducts['true'].map((product) => (
                            <ProductLink key={product.id} match={match} product={product} />
                          ))}
                        </>
                      )}

                      {groupedProducts['false'] && (
                        <>
                          <Heading level={3}>Fullførte bestillinger</Heading>
                          {groupedProducts['false'].map((product) => (
                            <ProductLink key={product.id} match={match} product={product} />
                          ))}
                        </>
                      )}
                    </nav>

                    {estate.customer.canCreateOrders && (
                      <CreateWebOrderButton estateId={estate.id} />
                    )}

                    {estate.createCollageUrl &&
                      <CreateCollageButton
                        collageUrl={estate.createCollageUrl}
                        text="Lag collage av leverte bilder"
                      />
                    }

                    {estate.canCreateAiStudio &&
                      <CreateAiStudioButton estateId={estate.id} />
                    }

                    <nav>
                      <Heading level={3}>Annet</Heading>
                      {estate.customer.enableAreaPhotos && (
                        <NavLink to={`${match.url}/omradebilder`}>
                          Kjøp bilder fra nærområdet
                        </NavLink>
                      )}
                      <NavLink to={`${match.url}/ordre`}>
                        Ordreoversikt
                      </NavLink>
                      <NavLink to={`${match.url}/deling`}>
                        Deling
                      </NavLink>
                      <NavLink to={`${match.url}/boliginfo`}>
                        Boliginformasjon
                      </NavLink>
                    </nav>
                  </>
                }
                content={children}
              />
            </Layout>
          </EstateContext.Provider>
        )
      }}
    </Query>
  )
}

EstateLayout.propTypes = {
  match: PropTypes.object.isRequired,
  children: PropTypes.node,
}

export default EstateLayout
